import { useId } from 'react';

type LogoVariant = 'full' | 'mark' | 'stacked';
type LogoColorScheme = 'color' | 'white' | 'mono';
type LogoSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

interface LogoProps {
  variant?: LogoVariant;
  colorScheme?: LogoColorScheme;
  size?: LogoSize;
  font?: string;
  style?: React.CSSProperties;
}

const sizes: Record<LogoSize, { height: number; markSize: number; fontSize: number }> = {
  xs: { height: 24, markSize: 24, fontSize: 16 },
  sm: { height: 32, markSize: 32, fontSize: 20 },
  md: { height: 48, markSize: 48, fontSize: 28 },
  lg: { height: 64, markSize: 64, fontSize: 36 },
  xl: { height: 96, markSize: 96, fontSize: 52 },
  xxl: { height: 128, markSize: 128, fontSize: 72 },
};

interface LogoMarkProps {
  width: number;
  height: number;
  colorScheme: LogoColorScheme;
  uniqueId: string;
}

function LogoMark({ width, height, colorScheme, uniqueId }: LogoMarkProps) {
  const isWhite = colorScheme === 'white';
  const isMono = colorScheme === 'mono';
  const bgFill = isWhite ? 'rgba(255,255,255,0.15)' : isMono ? '#0F172A' : `url(#grad-${uniqueId})`;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0, transition: 'all 300ms' }}
    >
      <defs>
        {!isWhite && !isMono && (
          <linearGradient id={`grad-${uniqueId}`} x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#6CB4EE" />
            <stop offset="100%" stopColor="#0F6CBD" />
          </linearGradient>
        )}
      </defs>
      <rect width="48" height="48" rx="11" fill={bgFill} />
      <circle cx="24" cy="24" r="16" fill="none" stroke="#FFFFFF" strokeWidth="1.25" opacity="0.35" />
      <circle cx="24" cy="24" r="11.5" fill="none" stroke="#FFFFFF" strokeWidth="1.25" opacity="0.65" />
      <path d="M 27 11 L 16 25 L 24 25 L 20 37 L 32 22 L 24 22 Z" fill="#FFFFFF" />
    </svg>
  );
}

interface WordmarkProps {
  fontSize: number;
  colorScheme: LogoColorScheme;
  font: string;
}

function Wordmark({ fontSize, colorScheme, font }: WordmarkProps) {
  const isWhite = colorScheme === 'white';
  const isMono = colorScheme === 'mono';
  const crewColor = isWhite ? '#FFFFFF' : isMono ? '#0F172A' : '#6CB4EE';
  const bitColor = isWhite ? '#FFFFFF' : isMono ? '#0F172A' : '#0F6CBD';

  return (
    <span
      style={{
        fontSize: `${fontSize}px`,
        fontFamily: font,
        fontWeight: 700,
        letterSpacing: '0',
        lineHeight: 1,
      }}
    >
      <span style={{ color: crewColor }}>Crew</span>
      <span style={{ color: bitColor }}>Bit</span>
    </span>
  );
}

export function Logo({
  variant = 'full',
  colorScheme = 'color',
  size = 'md',
  font = '"Plus Jakarta Sans", sans-serif',
  style = {},
}: LogoProps) {
  const uniqueId = useId().replace(/:/g, '');
  const currentSize = sizes[size];

  if (variant === 'mark') {
    return (
      <div style={style}>
        <LogoMark
          width={currentSize.markSize}
          height={currentSize.markSize}
          colorScheme={colorScheme}
          uniqueId={uniqueId}
        />
      </div>
    );
  }

  if (variant === 'stacked') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, ...style }}>
        <LogoMark
          width={currentSize.markSize}
          height={currentSize.markSize}
          colorScheme={colorScheme}
          uniqueId={uniqueId}
        />
        <Wordmark fontSize={currentSize.fontSize} colorScheme={colorScheme} font={font} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, ...style }}>
      <LogoMark
        width={currentSize.markSize}
        height={currentSize.markSize}
        colorScheme={colorScheme}
        uniqueId={uniqueId}
      />
      <Wordmark fontSize={currentSize.fontSize} colorScheme={colorScheme} font={font} />
    </div>
  );
}
